import React, { useState, useEffect } from 'react';
import './addNewBrand.css';

const AddNewBrand = () => {
  const [brands, setBrands] = useState([]);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newBrand, setNewBrand] = useState({
    name: '',
    categoryId: '',
    categoryName: '',
    logo: null,
    preview: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  // API configuration
  const API_BASE_URL = 'https://car.amar.co.jp/api/master';

  // Get token from localStorage
  const token = localStorage.getItem('adminAuthToken');

  // Filter brands based on search term
  useEffect(() => {
    if (searchTerm === '') {
      setFilteredBrands(brands);
    } else {
      const filtered = brands.filter(brand =>
        brand.carBrandName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredBrands(filtered);
    }
  }, [searchTerm, brands]);

  // Check if save button should be disabled
  useEffect(() => {
    const shouldDisable = !newBrand.name || !newBrand.categoryId;
    setIsSaveDisabled(shouldDisable);
  }, [newBrand.name, newBrand.categoryId]);

  // Fetch brands and categories from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        
        // Make parallel API calls
        const [categoriesRes, brandsRes] = await Promise.all([
          fetch(`${API_BASE_URL}/car-catgories/get-all-categories`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en',
              // 'Authorization': `Bearer ${token}`
            }
          }),
          fetch(`${API_BASE_URL}/car-brand/get-all-brandName`, {
            method: 'GET',
            headers: {
              'accept': 'application/json',
              'Accept-Language': 'en',
              // 'Authorization': `Bearer ${token}`
            }
          })
        ]);

        const categoriesData = await categoriesRes.json();
        const brandsData = await brandsRes.json();

        if (categoriesData.status === 200) {
          setCategories(categoriesData.data);
        }

        if (brandsData.code === 200) {
          setBrands(brandsData.carBrandList);
          setFilteredBrands(brandsData.carBrandList);
        }
      } catch (err) {
        setError('Failed to fetch data');
        console.error('API Error:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If category is being changed, find the category name
    if (name === 'categoryId') {
      const selectedCategory = categories.find(cat => cat.carCategoriesId === value);
      setNewBrand({ 
        ...newBrand, 
        [name]: value,
        categoryName: selectedCategory ? selectedCategory.categoriesName : ''
      });
    } else {
      setNewBrand({ ...newBrand, [name]: value });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newBrand.name || !newBrand.categoryId) {
      setError('Brand name and category are required');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      
      // Prepare the request body
      const requestBody = {
        brandName: newBrand.name,
        categorieName: newBrand.categoryName
      };

      // Make API call to save the brand
      const response = await fetch(`${API_BASE_URL}/car-brand/add-brand-name`, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const result = await response.json();

      if (result.code === 200) {
        // Show success message
        setSuccessMessage('Brand added successfully');
        
        // Hide success message after 2 seconds
        setTimeout(() => {
          setSuccessMessage('');
        }, 2000);
        
        // Reset form
        resetForm();
        
        // Refresh brands list
        await fetchBrands();
      } else {
        setError(result.message || 'Failed to add brand');
      }
    } catch (err) {
      setError('An error occurred while saving the brand');
      console.error('Save Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to fetch brands
  const fetchBrands = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/car-brand/get-all-brandName`, {
        method: 'GET',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      if (data.code === 200) {
        setBrands(data.carBrandList);
        setFilteredBrands(data.carBrandList);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const handleEdit = (brand) => {
    setEditingId(brand.carBrandName);
    setNewBrand({
      name: brand.carBrandName,
      categoryId: '',
      categoryName: '',
      logo: null,
      preview: ''
    });
  };

  const resetForm = () => {
    setNewBrand({ name: '', categoryId: '', categoryName: '', logo: null, preview: '' });
    setEditingId(null);
    setError('');
  };

  const handleDelete = async (brandName) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      try {
        setIsLoading(true);
        // Here you would typically make an API call to delete the brand
        // For now, we'll just update the local state
        const updatedBrands = brands.filter(brand => brand.carBrandName !== brandName);
        setBrands(updatedBrands);
        setFilteredBrands(updatedBrands);
      } catch (err) {
        setError('Failed to delete brand');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.carCategoriesId === categoryId);
    return category ? category.categoriesName : 'Unknown';
  };

  return (
    <div className="brand-admin-container">
      <h2 className="admin-header">Brand Management</h2>
      
      {/* Success Popup */}
      {successMessage && (
        <div className="success-popup">
          {successMessage}
        </div>
      )}
      
      <div className="admin-panel">
        <div className="form-section">
          <h3 className='h3-add-new-brand'>{editingId ? 'Edit Brand' : 'Add New Brand'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Brand Name</label>
              <input
                type="text"
                name="name"
                value={newBrand.name}
                onChange={handleInputChange}
                placeholder="Enter brand name"
                className="form-input"
              />
            </div>
            
            <div className="form-group">
              <label>Category</label>
              <select
                name="categoryId"
                value={newBrand.categoryId}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="">Select Category</option>
                {categories.map(category => (
                  <option key={category.carCategoriesId} value={category.carCategoriesId}>
                    {category.categoriesName}
                  </option>
                ))}
              </select>
            </div>
            
            {error && <div className="error-message">{error}</div>}
            
            <div className="form-actions">
              <button 
                type="submit" 
                className="submit-btn" 
                disabled={isLoading || isSaveDisabled}
              >
                {isLoading ? 'Processing...' : (editingId ? 'Update' : 'Add')}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="cancel-btn">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
        
        <div className="list-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search brands..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          <h3 className='h3-add-new-brand'>Existing Brands</h3>
          {isLoading && !brands.length ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="brand-list">
              {filteredBrands.length > 0 ? (
                filteredBrands.map(brand => (
                  <div key={brand.carBrandName} className="brand-item">
                    <div className="brand-info">
                      <div className="brand-details">
                        <span className="brand-name">{brand.carBrandName}</span>
                      </div>
                    </div>
                    <div className="brand-actions">
                      <button onClick={() => handleEdit(brand)} className="edit-btn">
                        Edit
                      </button>
                      <button onClick={() => handleDelete(brand.carBrandName)} className="delete-btn">
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-results">No brands found matching your search.</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddNewBrand;